import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import { baseUrl } from "../../../connections";

const EMPTY_ARRAY = [];
const USER_FILE_BASE_URL = baseUrl.replace(/\/api\/v1\/?$/, "");

const ensureString = (value) => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
};

const ensureLineArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => ensureString(item).trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return EMPTY_ARRAY;
};

const ensureImageArray = (value) => {
  if (!Array.isArray(value)) {
    return EMPTY_ARRAY;
  }

  return value
    .map((item) => ensureString(item).trim())
    .filter(Boolean)
    .slice(0, 3);
};

const ensureImageValue = (value) => ensureString(value).trim();

const resolveAssetUrl = (value) => {
  const asset = ensureString(value).trim();

  if (!asset) {
    return "";
  }

  if (/^https?:\/\//i.test(asset) || asset.startsWith("data:")) {
    return asset;
  }

  return `${USER_FILE_BASE_URL}/user/file/${asset}`;
};

const resolveDoYouKnowProfile = (payload) => {
  if (!payload) return null;
  if (Array.isArray(payload?.data)) return payload.data[0] ?? null;
  if (Array.isArray(payload?.items)) return payload.items[0] ?? null;
  if (Array.isArray(payload)) return payload[0] ?? null;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return payload;
};

const extractProfilePreview = (payload) => {
  const profile = resolveDoYouKnowProfile(payload);
  const raw =
    profile?.do_you_know ||
    profile?.do_you_know_profile ||
    profile?.store_profile_metadata ||
    profile?.profile_metadata ||
    profile ||
    {};
  return {
    profileImage: ensureImageValue(raw.profileImage),
    galleryImages: ensureImageArray(raw.galleryImages),
    bannerImage: ensureImageValue(raw.bannerImage),
    founderName: ensureString(
      raw.founder_name || raw.founderName || raw.founder?.name || raw.name,
    ),
    role: ensureString(raw.role || raw.designation),
    brandName: ensureString(raw.brand_name || raw.brandName || raw.store_name),
    tagline: ensureString(raw.tagline),
    basedIn: ensureString(raw.based_in || raw.basedIn),
    industry: ensureString(raw.industry),
    experience: ensureString(
      raw.experience || raw.yearsOfExperience || raw.experience_years,
    ),
    activeLocations: ensureString(
      raw.active_locations || raw.activeLocations || raw.location_count,
    ),
    passionSkill: ensureString(raw.passion_skill || raw.passionSkill),
    whyBusiness: ensureString(raw.why_business || raw.whyBusiness),
    currentLocations: ensureString(
      raw.current_locations || raw.currentLocations,
    ),
    growthVision: ensureString(raw.growth_vision || raw.growthVision),
    about: ensureString(raw.about),
    journey: ensureString(raw.journey),
    ventures: ensureLineArray(raw.ventures),
    achievements: ensureLineArray(raw.achievements),
    differentiators: ensureLineArray(
      raw.differentiators || raw.what_sets_apart || raw.whatSetsApart,
    ),
    philosophy: ensureString(raw.philosophy),
    impact: ensureString(raw.impact),
    timeline: ensureLineArray(raw.timeline),
    personalHighlights: ensureLineArray(
      raw.personal_highlights || raw.personalHighlights,
    ),
  };
};

const renderList = (title, items) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <h6 style={{ fontWeight: 700, marginBottom: 8 }}>{title}</h6>
      <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
        {items.map((item, index) => (
          <li key={`${title}-${index}`} style={{ marginBottom: 4 }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const renderBlock = (title, value) => {
  if (!value) {
    return null;
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <h6 style={{ fontWeight: 700, marginBottom: 8 }}>{title}</h6>
      <p style={{ margin: 0, color: "#374151", whiteSpace: "pre-wrap" }}>
        {value}
      </p>
    </div>
  );
};

const renderStatusCard = (heading, description, isLoading) => (
  <div
    style={{
      width: "100%",
      minHeight: 190,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: "28px 20px",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 58,
        height: 58,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        background: "rgba(46, 125, 50, 0.05)",
        border: "1px solid rgba(46, 125, 50, 0.18)",
        color: "#43a047",
      }}
    >
      {isLoading ? (
        <CircularProgress size={28} thickness={4.8} />
      ) : (
        <BrokenImageOutlinedIcon sx={{ fontSize: 28 }} />
      )}
    </div>
    <p
      style={{
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: "#344054",
      }}
    >
      {heading}
    </p>
    <p
      style={{
        margin: 0,
        fontSize: 14,
        color: "#667085",
      }}
    >
      {description}
    </p>
  </div>
);

const DoYouKnowPlaceholder = ({
  title = "Do you Know",
  isLoading = false,
  isConfigured = false,
  hasSelectedBrand = false,
  hasData = false,
  errorMessage = "",
  doYouKnowData = null,
}) => {
  const profile = extractProfilePreview(doYouKnowData);
  const hasPreviewContent = Boolean(
    profile?.profileImage ||
      profile?.bannerImage ||
      profile?.galleryImages?.length ||
      profile?.founderName ||
      profile?.brandName ||
      profile?.tagline ||
      profile?.basedIn ||
      profile?.industry ||
      profile?.experience ||
      profile?.activeLocations ||
      profile?.passionSkill ||
      profile?.whyBusiness ||
      profile?.currentLocations ||
      profile?.growthVision ||
      profile?.about ||
      profile?.journey ||
      profile?.ventures?.length ||
      profile?.achievements?.length ||
      profile?.differentiators?.length ||
      profile?.philosophy ||
      profile?.impact ||
      profile?.timeline?.length ||
      profile?.personalHighlights?.length,
  );

  let heading = `${title} content coming soon`;
  let description = "There is no mapped content available for this section yet.";

  if (!hasSelectedBrand) {
    heading = `Select a brand to load ${title}`;
    description =
      "Pick a store from the brand list first, then open this tab to view related content.";
  } else if (isLoading) {
    heading = `Loading ${title}`;
    description = "We are fetching the latest brand-specific content.";
  } else if (!isConfigured) {
    heading = `${title} is waiting for an endpoint`;
    description =
      "The API call is wired in, but the endpoint is still empty. Add REACT_APP_DO_YOU_KNOW_ENDPOINT to start loading data.";
  } else if (errorMessage) {
    heading = `Unable to load ${title}`;
    description =
      "The request did not complete successfully. Try another brand or check the configured endpoint.";
  }

  if (!hasData || !hasPreviewContent) {
    return renderStatusCard(heading, description, isLoading);
  }

  return (
    <div style={{ width: "100%" }}>
      {(profile.bannerImage || profile.profileImage) && (
        <div
          style={{
            position: "relative",
            marginBottom: 70,
          }}
        >
          {profile.bannerImage ? (
            <img
              src={resolveAssetUrl(profile.bannerImage)}
              alt="Profile banner"
              style={{
                width: "100%",
                height: 300,
                objectFit: "cover",
                borderRadius: 12,
                display: "block",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: 260,
                borderRadius: 12,
                background:
                  "linear-gradient(135deg, rgba(26,143,227,0.08), rgba(5,199,242,0.16))",
              }}
            />
          )}
          {profile.profileImage && (
            <img
              src={resolveAssetUrl(profile.profileImage)}
              alt="Profile"
              style={{
                position: "absolute",
                left: 24,
                bottom: -30,
                width: 150,
                height: 150,
                minWidth: 150,
                minHeight: 150,
                objectFit: "cover",
                borderRadius: 10,
                border: "5px solid #fff",
                boxShadow: "0 8px 24px rgba(15,23,42,0.2)",
                background: "#fff",
              }}
            />
          )}
        </div>
      )}

      <div
        style={{
          padding: "18px 20px",
          borderRadius: 14,
          background:
            "linear-gradient(135deg, rgba(26,143,227,0.08), rgba(5,199,242,0.16))",
          marginBottom: 20,
        }}
      >
        <h4 style={{ fontWeight: 800, margin: "0 0 4px" }}>
          {profile.founderName || profile.brandName || "Founder Profile"}
        </h4>
        <div style={{ fontWeight: 600, color: "#1f2937" }}>
          {[profile.role, profile.brandName].filter(Boolean).join(" | ")}
        </div>
        {profile.tagline && (
          <p style={{ margin: "10px 0 0", color: "#374151" }}>
            {profile.tagline}
          </p>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {[
          ["Based In", profile.basedIn],
          ["Industry", profile.industry],
          ["Experience", profile.experience],
          ["Active Locations", profile.activeLocations],
        ]
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div
              key={label}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "#6b7280",
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div style={{ fontWeight: 600, color: "#111827" }}>{value}</div>
            </div>
          ))}
      </div>

      {renderBlock("Passion / Skill", profile.passionSkill)}
      {renderBlock("Why This Business", profile.whyBusiness)}
      {renderBlock("Current Locations", profile.currentLocations)}
      {renderBlock("Growth Vision", profile.growthVision)}
      {renderBlock("About", profile.about)}
      {renderBlock("Journey", profile.journey)}
      {renderList("Ventures", profile.ventures)}
      {renderList("Achievements", profile.achievements)}
      {renderList("What Sets Them Apart", profile.differentiators)}
      {renderBlock("Philosophy", profile.philosophy)}
      {renderBlock("Impact & Influence", profile.impact)}
      {renderList("Timeline", profile.timeline)}
      {renderList("Personal Highlights", profile.personalHighlights)}

      {profile.galleryImages.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
            marginTop: 8,
          }}
        >
          {profile.galleryImages.map((image, index) => (
            <img
              key={`${image}-${index}`}
              src={resolveAssetUrl(image)}
              alt={`Gallery preview ${index + 1}`}
              style={{
                width: "100%",
                height: 180,
                objectFit: "cover",
                borderRadius: 12,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

DoYouKnowPlaceholder.propTypes = {
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  isConfigured: PropTypes.bool,
  hasSelectedBrand: PropTypes.bool,
  hasData: PropTypes.bool,
  errorMessage: PropTypes.string,
  doYouKnowData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
};

export default DoYouKnowPlaceholder;
